import { useTranslation } from 'react-i18next';

import type { GetTermDetailResponse } from '@/api/generated/schemas';
import { Badge, Field, Input } from '@/shared/ui';
import { ProductLink } from '../../../product';
import { SerieLink } from '../../internal/SerieLink';
import type { TermDetailFormApi } from '../useTermDetailForm';

interface TermBasicsFormProps {
  form: TermDetailFormApi;
  detail: GetTermDetailResponse;
  productId: string;
  serieId: string;
}

const RANGE_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

/** Rozsah termínu „1. 11. 2026 → 8. 11. 2026“ (read-only). */
function formatRange(from: string, to: string | null): string {
  const fromLabel = RANGE_FORMAT.format(new Date(from));
  if (!to) return fromLabel;
  return `${fromLabel} → ${RANGE_FORMAT.format(new Date(to))}`;
}

/**
 * Formulář „Základní údaje termínu“ (labely vlevo) nad TanStack Form. Editovatelný je pouze kód termínu;
 * datumy a počet nocí jsou readonly, seriál a produkt jsou readonly s tlačítkem pro otevření jejich detailu.
 */
export function TermBasicsForm({ form, detail, productId, serieId }: TermBasicsFormProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <div className="gap-x-phx-7 gap-y-phx-3 grid max-w-5xl grid-cols-1 md:grid-cols-2">
      <form.Field name="code">
        {(field) => {
          const error = field.state.meta.isTouched
            ? (field.state.meta.errors[0]?.message as string | undefined)
            : undefined;
          return (
            <Field layout="inline" label={t('termDetail.basics.code')} required error={error}>
              <Input
                value={field.state.value}
                onChange={(event) => field.handleChange(event.target.value)}
                onBlur={field.handleBlur}
                required
                invalid={Boolean(error)}
              />
            </Field>
          );
        }}
      </form.Field>

      <Field layout="inline" label={t('termDetail.basics.range')}>
        <div className="text-phx-md text-phx-text gap-phx-2 py-phx-2_5 flex items-center tabular-nums">
          <span>{formatRange(detail.dateFrom, detail.dateTo)}</span>
          {detail.nights != null ? (
            <span className="text-phx-text-muted">
              · {t('termDetail.basics.nights', { count: detail.nights })}
            </span>
          ) : null}
          {detail.isCancelled ? (
            <Badge tone="warning" appearance="soft">
              {t('termDetail.basics.cancelled')}
            </Badge>
          ) : null}
        </div>
      </Field>

      <Field layout="inline" label={t('termDetail.basics.serie')}>
        <div className="text-phx-md py-phx-2_5">
          <SerieLink productId={productId} serieId={serieId}>
            <span className="text-phx-text">{detail.serieName}</span>
          </SerieLink>
        </div>
      </Field>

      <Field layout="inline" label={t('termDetail.basics.product')}>
        <div className="text-phx-md py-phx-2_5">
          <ProductLink productId={productId}>
            <span className="text-phx-text">
              <span className="font-mono">{detail.productCode}</span> · {detail.productName}
            </span>
          </ProductLink>
        </div>
      </Field>
    </div>
  );
}
